/**
 * OTLP/HTTP JSON wire types.
 *
 * Hand-rolled deliberately — no third-party OTel packages. The collector only
 * exposes OTLP/HTTP JSON publicly (gRPC 4317 is in-cluster only), and the JSON
 * encoding is a stable, small surface. Importing an SDK would pull a tracer
 * provider, context manager and async-hooks shims that Workers cannot use.
 *
 * Field-name and encoding rules below are verified against the live collector,
 * not inferred from the spec:
 * - timestamps are unix NANOSECONDS as JSON *strings*
 * - intValue is a *string*, doubleValue is a number
 * - traceId/spanId are exact-length lowercase hex; wrong lengths are dropped
 *   silently by the collector, with no error in the response
 */

/** OTLP AnyValue. Only the variants we actually emit. */
export type OtlpAnyValue =
	| { stringValue: string }
	| { boolValue: boolean }
	/** NB: string, not number — OTLP JSON encodes 64-bit ints as strings. */
	| { intValue: string }
	| { doubleValue: number }
	| { arrayValue: { values: OtlpAnyValue[] } };

export type OtlpKeyValue = { key: string; value: OtlpAnyValue };

export type OtlpEvent = {
	timeUnixNano: string;
	name: string;
	attributes?: OtlpKeyValue[];
};

export type OtlpLink = {
	traceId: string;
	spanId: string;
	traceState?: string;
	attributes?: OtlpKeyValue[];
};

export type OtlpStatus = {
	code: SpanStatusCode;
	message?: string;
};

export type OtlpSpan = {
	traceId: string;
	spanId: string;
	parentSpanId?: string;
	name: string;
	kind: SpanKind;
	startTimeUnixNano: string;
	endTimeUnixNano: string;
	attributes?: OtlpKeyValue[];
	events?: OtlpEvent[];
	links?: OtlpLink[];
	status?: OtlpStatus;
};

export type OtlpScopeSpans = {
	scope: { name: string; version?: string; attributes?: OtlpKeyValue[] };
	spans: OtlpSpan[];
};

export type OtlpResourceSpans = {
	resource: { attributes: OtlpKeyValue[] };
	scopeSpans: OtlpScopeSpans[];
};

export type OtlpTracePayload = {
	resourceSpans: OtlpResourceSpans[];
};

/**
 * A non-empty partialSuccess means the collector accepted the request but
 * dropped spans — the HTTP status is still 200, so this is the only signal.
 */
export type OtlpTraceResponse = {
	partialSuccess?: {
		rejectedSpans?: string;
		errorMessage?: string;
	};
};

/** Verified numeric mapping — do not renumber. */
export const SpanKind = {
	INTERNAL: 1,
	SERVER: 2,
	CLIENT: 3,
	PRODUCER: 4,
	CONSUMER: 5,
} as const;
export type SpanKind = (typeof SpanKind)[keyof typeof SpanKind];

/** Verified numeric mapping — do not renumber. */
export const SpanStatusCode = {
	UNSET: 0,
	OK: 1,
	ERROR: 2,
} as const;
export type SpanStatusCode = (typeof SpanStatusCode)[keyof typeof SpanStatusCode];

/**
 * Severity numbers from the OTel logs data model. Reused here for span events
 * that carry log-like records — the collector has no logs pipeline, so
 * structured logs ride along as span events instead. See otel/README.md.
 */
export const SeverityNumber = {
	TRACE: 1,
	DEBUG: 5,
	INFO: 9,
	WARN: 13,
	ERROR: 17,
	FATAL: 21,
} as const;
export type SeverityNumber = (typeof SeverityNumber)[keyof typeof SeverityNumber];

/** Loose attribute input, normalised to OtlpKeyValue by attrs(). */
export type AttributeValue = string | number | boolean | bigint | null | undefined | readonly (string | number | boolean)[];
export type Attributes = Record<string, AttributeValue>;
